import type {CameraController} from 'virtual-tour-viewer/src/modules/React360Proxy';
import type {Quaternion, Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import CameraUtils from '../utils/CameraUtils';
import {ThreeJsQuaternion, Vector3} from 'virtual-tour-viewer/src/modules/React360Proxy';

const HALF_PI = Math.PI / 2;
interface Point {
    pitch: number;
    yaw: number;
    relative: boolean;
}

export default class PitchYawCameraController implements CameraController {
    static _identity: Quaternion = [0, 0, 0, 1];

    _shouldHoldGaze: boolean;
    _verticalBumper: number;
    _enabled: boolean;
    /**
     * Radians to rotate around the Y-axis.
     */
    _yaw: number;

    /**
     * Radians to rotate around the X-axis.
     */
    _pitch: number;
    /**
     * The previous time that the camera requested an update in milliseconds from
     * January 1, 1970, 00:00:00 UTC
     */
    _lastUpdateAt: number;
    /**
     * If provided, pitch and yaw will be applied to the
     * defaultRotation instead of the provided rotation in
     * #fillCameraProperties(...).
     */
    _useRelativeRotation: boolean;
    /**
     * Radians per second to rotate at.
     */
    _velocity: number;

    constructor({
        shouldHoldGaze = false,
        verticalBumper,
        useRelativeRotation = false,
        velocity = HALF_PI,
    }) {
        this.enable();
        this._shouldHoldGaze = shouldHoldGaze;
        this._verticalBumper = verticalBumper || 0;
        this._useRelativeRotation = useRelativeRotation;
        this._pitch = null;
        this._yaw = null;
        this._velocity = velocity;
    }

    enable() {
        this._enabled = true;
    }

    disable() {
        this._enabled = false;
    }

    setPitch(pitch: number) {
        this._pitch = pitch;
    }

    setYaw(yaw: number) {
        this._yaw = yaw;
    }

    pan(point: Point, velocity?: number) {
        this.setPitch(point.pitch);
        this.setYaw(point.yaw);
        this._velocity = velocity;
        this._useRelativeRotation = point.relative;
    }

    getYaw() {
        return this._yaw || 0;
    }

    getPitch() {
        return this._pitch || 0;
    }

    hasYaw() {
        return !!this._yaw;
    }

    hasPitch() {
        return !!this._pitch;
    }

    hasVelocity() {
        return !!this._velocity;
    }

    /**
     * Converts a millisecond offset to second offset.
     * @param time
     * @private
     */
    static _toSeconds(time) {
        return time / 1000;
    }

    _applyPitchYaw(rotation: Quaternion, yaw: number, pitch: number, maxRadians?: Number) {
        if (pitch > HALF_PI) {
            pitch = Math.PI - pitch;
        } else if (pitch < -HALF_PI) {
            pitch = -Math.PI - pitch;
        }

        let targetRotation: Quaternion;
        let deltaYaw;
        let deltaPitch = null;
        if (this._useRelativeRotation) {
            deltaPitch = pitch;
            deltaYaw = yaw;
            targetRotation = CameraUtils.rotateCamera(rotation, yaw, pitch);
        } else {
            targetRotation = CameraUtils.rotateCamera(
                PitchYawCameraController._identity,
                yaw,
                pitch
            );
            ({deltaYaw, deltaPitch} = PitchYawCameraController._getRelativePitchYaw(
                rotation,
                targetRotation
            ));
        }
        let newRotation;
        if (maxRadians) {
            /**
             * The following bounding of deltaYaw and deltaPitch forces
             * the interpolation to occur over the shortest difference in
             * pitch and yaw instead of taking the specified direction.
             */
            if (Math.abs(deltaYaw) > Math.PI) {
                deltaYaw = -Math.sign(deltaYaw) * (2 * Math.PI - Math.abs(deltaYaw));
            }

            if (Math.abs(deltaPitch) > Math.PI) {
                deltaPitch = -Math.sign(deltaPitch) * (2 * Math.PI - Math.abs(deltaPitch));
            }

            if (deltaPitch !== 0 || deltaYaw !== 0) {
                let targetDistanceRadians = CameraUtils.getVectorMagnitude([deltaYaw, deltaPitch]);
                let actualDistanceRadians = Math.min(targetDistanceRadians, maxRadians);
                let interpolationCoefficient = actualDistanceRadians / targetDistanceRadians;

                deltaYaw *= interpolationCoefficient;
                deltaPitch *= interpolationCoefficient;
            }

            newRotation = CameraUtils.rotateCamera(rotation, deltaYaw, deltaPitch);
        } else {
            newRotation = targetRotation;
        }

        if (this._verticalBumper) {
            if (newRotation[3] === 0) {
                return false;
            }
            const verticalTangent = newRotation[0] / newRotation[3];
            const maximumTangentAbs = Math.tan(this._verticalBumper / 2);
            // If the new rotation exceeds pitch limits
            if (
                (this._verticalBumper && verticalTangent >= maximumTangentAbs) ||
                verticalTangent <= -maximumTangentAbs
            ) {
                newRotation = CameraUtils.rotateCamera(rotation, deltaYaw, 0);
            }
        }

        return newRotation;
    }

    static _getRelativePitchYaw(rotation: Quaternion, targetRotation: Quaternion) {
        let currentQuaternion: ThreeJsQuaternion = new ThreeJsQuaternion().fromArray(rotation);
        let targetQuaternion: ThreeJsQuaternion = new ThreeJsQuaternion().fromArray(targetRotation);
        let currentVector = new Vector3(0, 0, -1).applyQuaternion(currentQuaternion);
        let targetVector = new Vector3(0, 0, -1).applyQuaternion(targetQuaternion);
        let currentEuler = CameraUtils.getEulerFromVector3(currentVector);
        let targetEuler = CameraUtils.getEulerFromVector3(targetVector);
        let currentYaw = currentEuler.y;
        let targetYaw = targetEuler.y;
        let currentPitch = currentEuler.x;
        let targetPitch = targetEuler.x;

        let deltaYaw = targetYaw - currentYaw;
        let deltaPitch = targetPitch - currentPitch;

        return {
            deltaYaw,
            deltaPitch,
        };
    }

    fillCameraProperties(position: Vec3, rotation: Quaternion): boolean {
        const now = Date.now();
        try {
            if (!this._enabled || !(this.hasYaw() || this.hasPitch())) {
                return false;
            }

            let pitch = this.getPitch();
            let yaw = this.getYaw();

            if (!this._shouldHoldGaze) {
                this.setYaw(null);
                this.setPitch(null);
            }

            let maxRadians = null;
            if (this.hasVelocity()) {
                let timeElapsedSeconds: number = PitchYawCameraController._toSeconds(
                    now - this._lastUpdateAt
                );
                maxRadians = this._velocity * timeElapsedSeconds;
            }

            let newRotation = this._applyPitchYaw(rotation, yaw, pitch, maxRadians);

            rotation[0] = newRotation[0];
            rotation[1] = newRotation[1];
            rotation[2] = newRotation[2];
            rotation[3] = newRotation[3];

            return true;
        } finally {
            this._lastUpdateAt = now;
        }
    }
}
